export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    token: string;
    header: string;
    payload: string;
    verify: string;
    secret: string;
    verifyBtn: string;
    verifyOk: string;
    verifyFail: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'JWT-Decoder',
    subtitle:
      'Dekodiere JSON Web Tokens und prüfe HS256-Signaturen — alles lokal im Browser. Token und Secret werden nicht gesendet.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    token: 'JWT-Token',
    header: 'Header',
    payload: 'Payload',
    verify: 'Signatur prüfen (HS256)',
    secret: 'Secret',
    verifyBtn: 'Prüfen',
    verifyOk: 'Signatur gültig.',
    verifyFail: 'Signatur ungültig.',
  },
  seo: {
    aboutTitle: 'JWTs sicher und lokal lesen',
    aboutBody:
      'Dieser Decoder liest JSON Web Tokens und prüft HS256-Signaturen ausschließlich lokal in deinem Browser über die Web Crypto API. Token und Secret werden nicht an einen Server gesendet oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Werden mein Token oder Secret gesendet?',
        a: 'Nein. Dekodierung und HS256-Prüfung laufen vollständig lokal.',
      },
      {
        q: 'Welche Signatur kann ich prüfen?',
        a: 'HS256 (HMAC-SHA256) mit deinem Secret. Ohne Secret wird der Token nur dekodiert, nicht verifiziert.',
      },
      {
        q: 'Ist das ein Generator?',
        a: 'Nein, es liest/prüft bestehende Tokens. Base64 findest du im separaten Base64-Tool.',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Alles läuft lokal im Browser.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'JWT Decoder',
    subtitle:
      'Decode JSON Web Tokens and verify HS256 signatures — all locally in your browser. Token and secret are never sent.',
    localBadge: '100% local · free',
  },
  ui: {
    token: 'JWT token',
    header: 'Header',
    payload: 'Payload',
    verify: 'Verify signature (HS256)',
    secret: 'Secret',
    verifyBtn: 'Verify',
    verifyOk: 'Signature valid.',
    verifyFail: 'Signature invalid.',
  },
  seo: {
    aboutTitle: 'Read JWTs securely and locally',
    aboutBody:
      'This decoder reads JSON Web Tokens and verifies HS256 signatures exclusively in your browser via the Web Crypto API. Token and secret are not sent to a server or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Are my token or secret sent anywhere?',
        a: 'No. Decoding and HS256 verification run entirely locally.',
      },
      {
        q: 'Which signature can I verify?',
        a: 'HS256 (HMAC-SHA256) with your secret. Without a secret the token is only decoded, not verified.',
      },
      {
        q: 'Is this a generator?',
        a: 'No, it reads/verifies existing tokens. For Base64 use the separate Base64 tool.',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Everything runs locally in your browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
